import React, { useEffect } from 'react';
import { CheckCircle } from 'lucide-react';

const CourierToEuropePage = () => {
  useEffect(() => {
    document.title = 'Send Courier from India to Europe | Shipping to Germany, France, Netherlands & More | XFas';
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute('content', 'Ship parcels & cargo from India to any European country with XFas Logistics. EU customs support, express & economy options, competitive rates. Germany, France, Netherlands, Italy & more.');
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <section className="bg-gray-900 text-white py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="text-5xl mb-4">🇪🇺</div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Send Courier from India to Europe</h1>
          <p className="text-xl text-orange-400 font-medium">Your Multichannel Gateway to the EU</p>
          <p className="mt-4 text-gray-300 text-lg">Delivering from India to 27 EU Nations + Norway, Switzerland & Beyond</p>
          <a href="/#get-quote" className="inline-block mt-8 bg-orange-500 hover:bg-orange-600 text-white font-semibold px-8 py-3 rounded-lg transition-colors">
            Get a Quote
          </a>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 py-16 space-y-16">

        {/* Why XFas */}
        <section>
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Why XFas for India to Europe Shipping?</h2>
          <ul className="space-y-3">
            {[
              ['🌍', 'Coverage across all EU member states plus Norway, Switzerland, and the UK'],
              ['✈️', 'Express delivery in 3–6 business days to major European cities'],
              ['🏷️', 'Economy options for budget-conscious shippers — ideal for large or heavy parcels'],
              ['📋', 'EU customs compliance support — EUR.1, commercial invoice, HS code guidance, and IOSS advisory for e-commerce'],
              ['📦', 'Parcels, documents, commercial cargo — all handled under one roof'],
              ['🤝', 'Strategic carrier partnerships ensure competitive rates and reliable transit times'],
            ].map(([icon, text]) => (
              <li key={text} className="flex gap-3 text-gray-600">
                <span className="flex-shrink-0">{icon}</span>
                <span>{text}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* Countries */}
        <section className="bg-gray-50 rounded-2xl p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Countries We Ship to in Europe</h2>
          <p className="text-gray-600 leading-relaxed">
            Germany · France · Netherlands · Italy · Spain · Belgium · Austria · Sweden · Denmark · Finland · Norway · Switzerland · Poland · Czech Republic · Portugal · Greece · Ireland · Hungary · Romania · and all other European destinations.
          </p>
        </section>

        {/* Shipping Options */}
        <section>
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Shipping Options: India to Europe</h2>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-gray-900 text-white">
                  <th className="text-left p-4 rounded-tl-lg">Service</th>
                  <th className="text-left p-4">Transit Time</th>
                  <th className="text-left p-4 rounded-tr-lg">Best For</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['Air Express', '3–6 Business Days', 'Documents, time-critical shipments'],
                  ['Economy Air', '5–8 Business Days', 'Parcels, gifts, personal effects'],
                  ['Commercial Cargo', 'Customised', 'Garment exporters, B2B trade'],
                ].map(([service, time, best], i) => (
                  <tr key={service} className={i % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                    <td className="p-4 font-semibold text-gray-800">{service}</td>
                    <td className="p-4 text-gray-600">{time}</td>
                    <td className="p-4 text-gray-600">{best}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* EU Customs */}
        <section className="bg-blue-50 rounded-2xl p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">EU Customs — What Indian Shippers Must Know</h2>
          <p className="text-gray-600 mb-4">Shipping into the European Union involves customs clearance under EU import regulations. XFas helps you navigate:</p>
          <ul className="space-y-2">
            {[
              'HS Code Classification — Correct tariff codes for your goods to avoid delays',
              'EU Customs Duty & VAT — Advisory on applicable rates by product and destination country',
              'IOSS Registration for E-Commerce — For Indian online sellers shipping low-value goods (under €150) to EU consumers, IOSS simplification can be applied',
              'Prohibited Goods Guidance — Country-specific restrictions and EU-wide prohibitions explained clearly',
              'Commercial Invoice & Packing List preparation support',
            ].map((item) => (
              <li key={item} className="flex gap-3">
                <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                <span className="text-gray-600">{item}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* Popular Routes */}
        <section>
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Most Popular India–Europe Shipping Routes</h2>
          <div className="space-y-3">
            {[
              ['India to Germany', 'Deliver to Berlin, Munich, Frankfurt, Hamburg, and all German postal codes'],
              ['India to France', 'Express and economy service to Paris, Lyon, Marseille, and beyond'],
              ['India to Netherlands', 'Popular route for Indian IT professionals and businesses'],
              ['India to Italy', 'Serving the large Indian community across Milan, Rome, and Turin'],
              ['India to Spain', 'Door-to-door delivery to Madrid, Barcelona, and all Spanish regions'],
            ].map(([route, desc]) => (
              <div key={route} className="flex gap-3 items-start">
                <CheckCircle className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
                <span className="text-gray-600"><span className="font-semibold text-gray-800">{route}</span> — {desc}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Ideal For */}
        <section>
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Ideal For</h2>
          <div className="grid md:grid-cols-2 gap-3">
            {[
              '🇮🇳 Indian diaspora sending gifts and personal parcels home',
              '🛍️ Indian e-commerce brands selling to European customers',
              '🏭 Textile and handicraft exporters serving European buyers',
              '💼 Businesses shipping commercial samples or trade documents',
              '🎓 Students and professionals relocating to Europe from India',
            ].map((item) => (
              <div key={item} className="bg-gray-50 rounded-lg p-3 text-gray-600">
                {item}
              </div>
            ))}
          </div>
        </section>

        {/* FAQ */}
        <section>
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Frequently Asked Questions</h2>
          <div className="space-y-6">
            {[
              {
                q: 'How long does a courier from India to Germany take?',
                a: 'Express services deliver to Germany in 3–5 business days. Economy services typically take 5–8 business days.',
              },
              {
                q: "Is EU customs clearance included in XFas's Europe shipping service?",
                a: 'XFas provides full documentation assistance and customs advisory for all European shipments. The applicable duties and taxes at the destination are the responsibility of the consignee unless a DDP (Delivered Duty Paid) arrangement is made.',
              },
              {
                q: 'Can Indian e-commerce sellers use XFas to ship to European buyers?',
                a: 'Absolutely. XFas has specific solutions for D2C and marketplace sellers shipping from India to EU customers, including IOSS compliance guidance and return logistics options.',
              },
            ].map(({ q, a }) => (
              <div key={q} className="border-b border-gray-200 pb-6">
                <p className="font-semibold text-gray-800 mb-2">{q}</p>
                <p className="text-gray-600">{a}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Related links */}
        <section className="bg-gray-50 rounded-2xl p-6">
          <p className="text-gray-600 text-sm">Also shipping to: <a href="/send-courier-from-india-to-usa/" className="text-orange-500 hover:underline font-medium">United States</a> · <a href="/send-courier-from-india-to-uk/" className="text-orange-500 hover:underline font-medium">United Kingdom</a></p>
        </section>

        {/* CTA */}
        <section className="text-center bg-orange-500 rounded-2xl py-12 px-6 text-white">
          <h2 className="text-2xl font-bold mb-3">Ready to Ship to Europe?</h2>
          <p className="mb-6 text-orange-100">Get an instant quote and compare carrier rates in seconds.</p>
          <a href="/#get-quote" className="inline-block bg-white text-orange-500 font-semibold px-8 py-3 rounded-lg hover:bg-orange-50 transition-colors">
            Get a Quote
          </a>
        </section>

      </div>
    </div>
  );
};

export default CourierToEuropePage;
